import { Component, ElementRef, OnInit, ViewChild, HostListener } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CollectionsService } from 'src/app/services/collections.service';
import { AddCollectionDialogComponent } from '../add-collection-dialog/add-collection-dialog.component';
import { EditCollectionDialogComponent } from '../edit-collection-dialog/edit-collection-dialog.component';
import { lastValueFrom, Subject } from 'rxjs';
import { UploadProgressSubject } from 'src/app/shared/fork.join.progress';
import { User } from 'src/app/models/user.interface';
import { Collection, CommonDimensions, FileMetadata, RowData } from 'src/app/models/collection.interface';
import { UserService } from 'src/app/services/user.service';
import { MatAutocompleteTrigger } from '@angular/material/autocomplete';
import { FilterModalNewComponent } from 'src/app/filter-modal/filter-modal.component';
import { CollectionAttributes, FilterSets } from 'src/app/models/advisor.interface';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, themeMaterial, RowSelectionOptions, RowSelectedEvent } from 'ag-grid-community';
import { RowDetailsComponent } from 'src/app/collections/row-details/row-details.component';
import { EditNameDialogComponent } from './edit-name-dialog/edit-name-dialog.component';
import { EditPrivacyDialogComponent } from './edit-privacy-dialog/edit-privacy-dialog.component';
import { ProgressCellComponent } from '../../shared/progress-cell/progress-cell.component';

const SNACKBAR_SELECT_FILES = 'Please select files to upload.';
const SNACKBAR_SELECT_COLLECTION = 'Please select a collection';
const SNACKBAR_UPLOAD_SUCCESS = 'Files uploaded successfully! Ingestion takes ~15 minutes per 50 pages';
const SNACKBAR_UPLOAD_FAIL = 'Upload failed';
const SNACKBAR_COLLECTION_FETCH_FAIL = 'Error fetching collection data. Please try again.';
const SNACKBAR_COLLECTION_CREATED = 'Collection created successfully';
const SNACKBAR_COLLECTION_CREATE_FAIL = 'Error creating collection';
const SNACKBAR_COLLECTION_NAME_UPDATED = 'Collection name updated successfully';
const SNACKBAR_COLLECTION_NAME_UPDATE_FAIL = 'Error updating collection name';
const SNACKBAR_COLLECTION_UPDATED = 'Collection updated successfully';
const SNACKBAR_COLLECTION_UPDATE_FAIL = 'Error updating collection';
const SNACKBAR_COLLECTION_PRIVACY_UPDATED = 'Collection privacy updated successfully';
const SNACKBAR_COLLECTION_PRIVACY_UPDATED_FAIL = 'Error updating collection privacy';
const SNACKBAR_DURATION_SHORT = 3000;
const SNACKBAR_DURATION_LONG = 5000;
const LOCAL_STORAGE_KEY = 'selected_collection';

@Component({
  selector: 'app-manage-collections',
  templateUrl: './manage-collections.component.html',
  styleUrls: ['./manage-collections.component.scss']
})
export class ManageCollectionsComponent implements OnInit {
  @ViewChild('agGrid') agGrid!: AgGridAngular; // Inject agGrid component
  @ViewChild('fileInput') fileInput!: ElementRef;
  @ViewChild(MatAutocompleteTrigger) autocompleteTrigger!: MatAutocompleteTrigger; // Reference to autocomplete trigger

  collections: Collection[] = []; // All collections (dropdown options, also where dimensions live)
  // Autocomplete functionality
  filteredCollections: Collection[] = []; // Filtered collections for autocomplete
  collectionInput: Collection | null = {} as Collection; // Input for autocomplete
  rowData: RowData[] = []; // Stores all file data for grid
  filteredRowData: RowData[] = []; // For showing search on grid--this is for display only
  colDefs: ColDef[] = this.getBaseColDefs(); // Column headers
  // Grid settings
  rowSelection: RowSelectionOptions | 'single' | 'multiple' = {
    mode: 'multiRow',
    selectAll: 'filtered',
    enableClickSelection: true,
    isRowSelectable: node => !this.isRowLocked(node.data)
  };
  // Store grid-level selected rows, this is passed to selectedDocumentsMenu
  selectedRows: CommonDimensions[] = [];
  selectedCollection: Collection | undefined = undefined; // Active dropdown selection
  collectionSelectOptions: CollectionAttributes = {}; // For filtering via modal
  theme = themeMaterial; // Theme for ag-grid
  activeFilters: CollectionAttributes = {
    state: [],
    year: [],
    docket_number: []
  };
  searchMode = false; // Triggers search UI
  gridFilterText = ''; // Text for quick filter
  selectedFiles: FileList | null = null;
  uploadInProgress = false;
  users: User[] = []; // for access lists on this page
  loadingUsers = false;
  userActionInProgress = false;
  usersErrorMessage = '';
  uploadProgress = 0;
  uploadProgressArray: number[] = [];
  progressSubject = new Subject<UploadProgressSubject>();
  frameworkComponents = {
    progressCellRenderer: ProgressCellComponent
  };

  constructor(
    private snackBar: MatSnackBar,
    private dialog: MatDialog,
    private collectionsService: CollectionsService,
    private userService: UserService,
    private elementRef: ElementRef
  ) {}

  ngOnInit(): void {
    this.getCollections();
    this.getUsers(); // require users (for manipulating access lists)
    this.subscribeToUploadProgress();
  }

  subscribeToUploadProgress() {
    this.progressSubject.asObservable().subscribe(progress => {
      this.uploadProgressArray[progress.index] = progress.progress;
      const sum = this.uploadProgressArray.reduce((accumulator, currentValue) => accumulator + currentValue, 0);
      const overallProgress = sum / progress.total;
      this.uploadProgress = overallProgress;
      console.debug(
        `file upload progress -- [ fileIndex: ${progress.index}, fileProgress: ${progress.progress}, overallProgress: ${overallProgress} ]`
      );
    });
  }
  // Controls the visibility of the search input field
  toggleSearchMode(event: Event, flag: boolean) {
    event.stopPropagation();
    this.searchMode = flag;
    // Reset the grid filter text upon entering search mode
    if (flag) {
      this.setGridFilterText('');
    }
  }
  // Quick filter that filters all columns
  setGridFilterText(text: string) {
    this.gridFilterText = text;
    if (this.agGrid.api) {
      this.agGrid.api.updateGridOptions({
        quickFilterText: this.gridFilterText
      });
    }
  }
  getCollections() {
    this.collectionsService.getCollections(true).subscribe({
      next: data => {
        this.collections = data;
        // Sort collections by name in alphabetical order
        this.filteredCollections = [...this.collections]; // Initialize filtered collections
        let lastActiveCollection = this.collections.find(c => c.name === localStorage.getItem(LOCAL_STORAGE_KEY) || '');
        if (!lastActiveCollection) {
          // No session found, set default to top collection
          lastActiveCollection = this.collections[0];
        }
        this.setCollection(lastActiveCollection);
      },
      error: error => {
        console.error('There was an error while fetching the collections: ', error);
      }
    });
  }

  toggleCollectionVisibility(collection: Collection | undefined) {
    if (!collection) {
      return;
    }
    const updatedCollection = { ...collection, is_hidden: !collection.is_hidden };
    this.collectionsService.updateCollection(collection.collection_id, updatedCollection).subscribe({
      next: () => {
        this.snackBar.open(`Collection ${updatedCollection.is_hidden ? 'hidden' : 'shown'}`, 'Close', {
          duration: SNACKBAR_DURATION_SHORT
        });
        this.selectedCollection = updatedCollection;
        this.getCollections();
      },
      error: error => {
        console.error('Error updating collection visibility:', error);
        this.snackBar.open('Error updating collection visibility', 'Close', {
          duration: SNACKBAR_DURATION_SHORT
        });
      }
    });
  }
  setCollectionToLocalStorage(collectionName: string) {
    localStorage.setItem(LOCAL_STORAGE_KEY, collectionName);
  }

  // Close the search mode when clicking outside the component
  @HostListener('document:click', ['$event'])
  clickOutside(event: Event) {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.toggleSearchMode(event, false);
    }
  }

  // Collections Search box
  clearInputText() {
    this.collectionInput = null;
    this.filteredCollections = [...this.collections];
  }

  getBaseColDefs(): ColDef[] {
    return [
      {
        field: 'title'
      },
      {
        field: 'original_document_uri',
        hide: true,
        tooltipField: 'original_document_uri'
      },
      { field: 's3_output_path', hide: true, tooltipField: 's3_output_path' },
      {
        field: 'collection_id',
        filter: true,
        hide: true,
        tooltipField: 'collection_id'
      },
      {
        field: 'file_id',
        filter: true,
        hide: true
      },
      { field: 'collection_name', hide: true, tooltipField: 'collection_name' }
    ];
  }
  mergeColDefs = (base: ColDef[], prioritized: ColDef[]) => {
    const columnMap = new Map();
    base.forEach(col => columnMap.set(col.field, col));
    // Override with prioritized columns (existing fields are replaced)
    prioritized.forEach(col => columnMap.set(col.field, col));
    return Array.from(columnMap.values());
  };
  // Update column definitions based on selected collection
  updateColDefs(collection: Collection) {
    const dimensions = collection ? collection.dimensions : [];
    const standardizedDimensions = dimensions.map(dimension => dimension.name.replace(/\s+/g, '_').toLowerCase());

    // Visible columns (the rest goes into RowDetailsComponent)
    const prioritizedColumns = ['title', 'phase', 'state', 'industry', 'company', 'year'];

    // Adjust column widths - make title larger, year smaller
    const columnWidths: Record<string, number> = {
      title: 3,
      year: 0.7
    };

    const prioritizedColDefs = prioritizedColumns
      .filter(dim => standardizedDimensions.includes(dim))
      .map(dim => ({
        field: dim,
        headerName: dim
          .replace(/_/g, ' ')
          .toLowerCase()
          .replace(/\b\w/g, char => char.toUpperCase()),
        tooltipField: dim,
        // Set width based on predefined widths or default to 1
        flex: columnWidths[dim] || 1
      }));

    this.colDefs = this.mergeColDefs(this.getBaseColDefs(), prioritizedColDefs).concat([
      {
        field: '',
        width: 60,
        cellRenderer: RowDetailsComponent,
        cellRendererParams: {
          adminMode: true, // Always show admin mode in manage collections
          onRowUpdate: this.handleRowUpdate.bind(this)
        }
      }
    ]);

    const mergedColDefs = this.mergeColDefs(this.getBaseColDefs(), prioritizedColDefs);

    // Find and update the existing 'title' column
    const titleCol = mergedColDefs.find(col => col.field === 'title');
    if (titleCol) {
      titleCol.cellRenderer = ProgressCellComponent;
    }

    this.colDefs = mergedColDefs.concat([
      {
        field: '',
        width: 60,
        cellRenderer: RowDetailsComponent,
        cellRendererParams: {
          adminMode: true,
          onRowUpdate: this.handleRowUpdate.bind(this)
        }
      }
    ]);

    if (this.agGrid.api) {
      this.agGrid.api.updateGridOptions({
        columnDefs: this.colDefs
      });
    }
  }

  setCollection(collection: Collection) {
    if (!collection) return;

    this.selectedCollection = collection;
    this.collectionInput = collection;

    const cidBare = this.fileNameFromFileId(collection.collection_id);
    this.syncPinnedToStorage(cidBare); // restore pinned rows first

    this.fetchCollectionData(collection); // then fetch live rows

    this.setCollectionToLocalStorage(collection.name);
    this.snackBar.open(`Selected ${collection.name}`, 'Close', { duration: SNACKBAR_DURATION_SHORT });
  }

  processRowDataForFilters(rowData: RowData[], validFilters: FilterSets) {
    rowData.forEach((item: RowData) => {
      // Only process keys in validFilters
      Object.entries(item).forEach(([dimension, value]) => {
        // Remove null values to avoid having blank as a filter option
        if (value && dimension in validFilters) {
          validFilters[dimension].add(value);
        }
      });
    });
  }

  private setToSortedArray(set: Set<string>): string[] {
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }

  // Filter functionality
  // Populate filters associated with the selected collection
  fetchFilterOptions(collection: Collection) {
    const validFilters: FilterSets = {};

    // Populate validFilters for this collection
    collection.dimensions.forEach(dimension => {
      const standardized_name = dimension.name.replace(/\s+/g, '_').toLowerCase();
      validFilters[standardized_name] = new Set<string>();
    });

    // Apply filters to rowData
    if (this.rowData.length > 0) {
      this.processRowDataForFilters(this.rowData, validFilters);
    } else {
      // If rowData is not available yet (unlikely)
      console.warn('Attempting to fetch filter options before row data is available');
    }

    // Convert set to sorted array
    Object.keys(validFilters).forEach(key => {
      this.collectionSelectOptions[key] = this.setToSortedArray(validFilters[key]);
    });
  }

  get hasFilterOptions(): boolean {
    return Object.keys(this.collectionSelectOptions).length > 0;
  }
  // Show # of filters being applied to grid
  get filterCount(): number {
    return Object.values(this.activeFilters).flat().length;
  }
  onQuickFilterChanged(event: Event) {
    const input = event.target as HTMLInputElement;
    this.setGridFilterText(input.value);
  }

  // Filter collections based on user input
  filterCollections(event: Event): void {
    const filterValue = (event.target as HTMLInputElement).value.toLowerCase();

    // Filter collections based on the input value
    this.filteredCollections = this.collections.filter(collection =>
      collection.name.toLowerCase().includes(filterValue)
    );

    // If no collections match and we have a selected collection, add it to the filtered list
    if (this.filteredCollections.length === 0 && this.selectedCollection) {
      if (this.selectedCollection.name.toLowerCase().includes(filterValue)) {
        this.filteredCollections = [this.selectedCollection];
      }
    }
  }

  // Table Functionality
  showLoading(flag = true) {
    if (this.agGrid.api) {
      this.agGrid.api.setGridOption('loading', flag);
    }
  }

  fetchCollectionData(collection: Collection) {
    this.showLoading(true);
    const cidBare = this.fileNameFromFileId(collection.collection_id);
    const yearFrom = (d?: string) => (d || '').match(/\b(19|20)\d{2}\b/)?.[0] ?? '';

    this.collectionsService.getCollectionData(collection.collection_id).subscribe({
      next: data => {
        const mapped: RowData[] = data.map((item: FileMetadata) => {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          if ((item as any).is_cloned_from_library === true) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const cat = (item.catalogue || {}) as any;

            const title =
              cat.document_title?.trim?.() ||
              cat.title?.trim?.() ||
              this.fileNameFromUri(item.original_document_uri) ||
              (item.file_id?.includes('#') ? item.file_id.split('#')[1] : item.file_id) ||
              'Document';

            return {
              collection_id: item.collection_id,
              file_id: item.file_id,
              processing_stage: '', // no progress bar, not “locked”
              original_document_uri: item.original_document_uri ?? '',
              s3_output_path: item.s3_output_path ?? '',
              company: cat.utility_company ?? '',
              state: cat.state ?? '',
              year: yearFrom(cat.filing_date),
              title,
              collection_name: collection.name
            } as RowData;
          }

          if (item.catalogue) {
            return {
              ...item.catalogue,
              original_document_uri: item.original_document_uri,
              s3_output_path: item.s3_output_path,
              collection_id: item.collection_id,
              file_id: item.file_id,
              collection_name: collection.name,
              processing_stage: item.processing_stage ?? ''
            };
          }

          const fallBackTitle =
            this.fileNameFromFileId(item.file_id) ||
            this.fileNameFromUri(item.original_document_uri) ||
            'Upload in Progress...';

          return {
            collection_id: item.collection_id,
            file_id: item.file_id,
            processing_stage: item.processing_stage ?? '',
            original_document_uri: item.original_document_uri ?? '',
            s3_output_path: item.s3_output_path ?? '',
            company: '',
            state: '',
            year: '',
            title: fallBackTitle
          } as RowData;
        });

        // remove storage + pinned row for any row now ingesting
        const ingesting = new Set(['started', 'image_extraction', 'text_extraction', 'failed']);
        for (const r of mapped) {
          if (ingesting.has((r.processing_stage || '').toLowerCase())) {
            this.removePlaceholderId(cidBare, r.file_id); // exact file_id match
          }
        }

        this.rowData = mapped;
        this.filteredRowData = mapped;

        this.updateColDefs(collection);
        this.fetchFilterOptions(collection);
      },
      error: err => {
        console.error('There was an error while fetching the collections: ', err);
        this.agGrid.api.hideOverlay();
        this.snackBar.open(SNACKBAR_COLLECTION_FETCH_FAIL, 'Close', { duration: SNACKBAR_DURATION_SHORT });
      },
      complete: () => this.showLoading(false)
    });
  }

  getUsers() {
    this.userService.getUsers().subscribe({
      next: (data: User[]) => {
        this.users = data.map(
          (user: User): User => ({
            user_id: user.CognitoUserId ?? '',
            email: user.Attributes?.find(attr => attr.Name === 'email')?.Value ?? user.Username ?? '',
            isAdmin: user.Attributes?.find(attr => attr.Name === 'custom:isAdmin')?.Value === 'true'
          })
        );
      },
      error: error => {
        console.error('Error fetching users:', error);
        this.usersErrorMessage = 'Error fetching users. Please try again.';
      },
      complete: () => {
        this.loadingUsers = false;
      }
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) this.selectedFiles = input.files;

    if (this.selectedCollection && this.selectedFiles?.length) {
      const cidBare = this.fileNameFromFileId(this.selectedCollection.collection_id);
      const newIds = Array.from(this.selectedFiles).map(f => `file#${f.name}`);

      const existing = this.loadPlaceholderIds(cidBare);
      const dedup = Array.from(new Set([...existing, ...newIds]));

      this.savePlaceholderIds(cidBare, dedup);
      this.pinnedTopRows = this.toPinnedRows(dedup); // show immediately
    }

    this.onSubmit(); // unchanged
  }

  onSubmit(): void {
    if (!this.selectedFiles) {
      this.snackBar.open(SNACKBAR_SELECT_FILES, 'Close', {
        duration: SNACKBAR_DURATION_SHORT
      });
      return;
    }

    if (!this.selectedCollection) {
      this.snackBar.open(SNACKBAR_SELECT_COLLECTION, 'Close', {
        duration: SNACKBAR_DURATION_SHORT
      });
      return;
    }
    this.uploadInProgress = true;
    this.uploadProgress = 0;

    this.collectionsService
      .uploadFilesToCollection(this.selectedCollection.collection_id, this.selectedFiles, this.progressSubject)
      .subscribe({
        next: () => {
          this.snackBar.open(SNACKBAR_UPLOAD_SUCCESS, 'Close', { duration: SNACKBAR_DURATION_LONG });
          this.getCollections(); // Refresh collection list
          this.uploadInProgress = false;
        },
        error: error => {
          console.error('Upload failed', error);
          this.snackBar.open(SNACKBAR_UPLOAD_FAIL, 'Close', { duration: SNACKBAR_DURATION_SHORT });
          this.uploadInProgress = false;
        }
      });
  }

  displayCollectionFn(collection: Collection): string {
    return collection && collection.name ? collection.name : '';
  }

  // Prevent 'Create New Collection' from firing when using Enter to select a collection
  onEnter(e: Event) {
    e.preventDefault();
  }
  // Select the top collection from the filtered list when Enter key is pressed
  selectTopCollection(event: Event): void {
    event.preventDefault();

    // If there are filtered collections, select the first one
    if (this.filteredCollections.length > 0) {
      const topCollection = this.filteredCollections[0];
      this.setCollection(topCollection);

      if (this.autocompleteTrigger) {
        this.autocompleteTrigger.closePanel();
      }
    }
  }

  openAddCollectionDialog(): void {
    const dialogRef = this.dialog.open(AddCollectionDialogComponent, {
      width: '800px',
      data: { users: this.users }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.collectionsService.createNewCollection(result).subscribe({
          next: new_collection => {
            this.getCollections();
            this.setCollection(new_collection);
            this.snackBar.open(SNACKBAR_COLLECTION_CREATED, 'Close', {
              duration: SNACKBAR_DURATION_SHORT
            });
          },
          error: error => {
            this.userActionInProgress = false;
            console.error('Error creating collection:', error);
            this.snackBar.open(SNACKBAR_COLLECTION_CREATE_FAIL, 'Close', {
              duration: SNACKBAR_DURATION_SHORT
            });
          }
        });
      }
    });
  }

  get showSelectedDocumentsMenu() {
    return this.selectedRows.length > 0;
  }

  // UNUSED: Might come in handy later
  onRowSelected(event: RowSelectedEvent): void {
    this.selectedRows = this.agGrid.api.getSelectedRows();
    const currentRow = event.node.data;
    console.log('Selected row:', currentRow);
  }

  openEditCollectionNameDialog(): void {
    if (!this.selectedCollection) return;
    const dialogRef = this.dialog.open(EditNameDialogComponent, {
      width: '470px',
      data: { collection: this.selectedCollection }
    });

    dialogRef.afterClosed().subscribe(updatedName => {
      if (updatedName && this.selectedCollection?.collection_id) {
        const collectionId = this.selectedCollection.collection_id;
        const updatedCollection = { ...this.selectedCollection, name: updatedName };
        this.collectionsService.updateCollection(collectionId, updatedCollection).subscribe({
          next: () => {
            // Save progress then refresh
            this.setCollectionToLocalStorage(updatedName);
            this.selectedCollection = updatedCollection;
            this.snackBar.open(SNACKBAR_COLLECTION_NAME_UPDATED, 'Close', {
              duration: SNACKBAR_DURATION_SHORT
            });
          },
          error: error => {
            this.userActionInProgress = false;
            console.error('Error updating collection:', error);
            this.snackBar.open(SNACKBAR_COLLECTION_NAME_UPDATE_FAIL, 'Close', {
              duration: SNACKBAR_DURATION_SHORT
            });
          },
          complete: () => {
            this.getCollections();
          }
        });
      }
    });
  }
  openEditCollectionPrivacyDialog(): void {
    if (!this.selectedCollection) return;
    const dialogRef = this.dialog.open(EditPrivacyDialogComponent, {
      width: '470px',
      data: { collection: this.selectedCollection, users: this.users }
    });

    dialogRef.afterClosed().subscribe((updatedCollection: Collection | undefined) => {
      if (!updatedCollection) return;
      this.collectionsService.updateCollection(updatedCollection.collection_id, updatedCollection).subscribe({
        next: () => {
          // Save progress then refresh
          this.setCollectionToLocalStorage(updatedCollection.name);
          this.selectedCollection = updatedCollection;
          this.snackBar.open(SNACKBAR_COLLECTION_PRIVACY_UPDATED, 'Close', {
            duration: SNACKBAR_DURATION_SHORT
          });
        },
        error: error => {
          this.userActionInProgress = false;
          console.error('Error updating collection:', error);
          this.snackBar.open(SNACKBAR_COLLECTION_PRIVACY_UPDATED_FAIL, 'Close', {
            duration: SNACKBAR_DURATION_SHORT
          });
        },
        complete: () => {
          this.getCollections();
        }
      });
    });
  }

  openEditCollectionDialog(): void {
    if (!this.selectedCollection) return;
    const dialogRef = this.dialog.open(EditCollectionDialogComponent, {
      width: '800px',
      data: { users: this.users, collection: this.selectedCollection }
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        this.selectedCollection = result;
        this.collectionsService.updateCollection(result.collection_id, result).subscribe({
          next: () => {
            this.getCollections();
            this.selectedCollection = result;
            this.snackBar.open(SNACKBAR_COLLECTION_UPDATED, 'Close', {
              duration: SNACKBAR_DURATION_SHORT
            });
          },
          error: error => {
            this.userActionInProgress = false;
            console.error('Error updating collection:', error);
            this.snackBar.open(SNACKBAR_COLLECTION_UPDATE_FAIL, 'Close', {
              duration: SNACKBAR_DURATION_SHORT
            });
          }
        });
      }
    });
  }

  // Display function for autocomplete
  displayFn(collection: Collection | null): string {
    // When a collection is selected, display its name
    return collection ? collection.name : '';
  }

  // Invoked from RowDetailsComponent (via ag-grid cellRenderer callback)
  handleRowUpdate(fileId: string, updatedData: CommonDimensions): void {
    console.log('Handling row update:', { fileId, updatedData });
    if (!this.selectedCollection) {
      this.snackBar.open(SNACKBAR_SELECT_COLLECTION, 'Close', {
        duration: SNACKBAR_DURATION_SHORT
      });
      return;
    }

    const urlSafeFileId = fileId.split('#')[1] || fileId; // Pass in the file_id without the hash
    this.collectionsService
      .updateFileMetadata(this.selectedCollection.collection_id, urlSafeFileId, updatedData)
      .subscribe({
        next: response => {
          console.log('File metadata updated successfully:', response);
          this.snackBar.open('Row updated successfully', 'Close', {
            duration: SNACKBAR_DURATION_SHORT
          });
          // Refresh the grid data
          if (this.selectedCollection) {
            this.fetchCollectionData(this.selectedCollection);
          }
        },
        error: error => {
          console.error('Error updating row:', error);
          this.snackBar.open('Error updating row', 'Close', {
            duration: SNACKBAR_DURATION_SHORT
          });
        }
      });
  }

  // Filter collections based on user Filter Modal select
  async openFilterModal(): Promise<void> {
    const dialogRef = this.dialog.open(FilterModalNewComponent, {
      width: '500px',
      data: {
        options: this.collectionSelectOptions,
        currentFilters: { ...this.activeFilters }
      }
    });

    const emittedFilters = await lastValueFrom(dialogRef.afterClosed());
    return this.updateRowDataFromFilters(emittedFilters);
  }

  updateRowDataFromFilters(filters: CollectionAttributes): void {
    if (filters) {
      console.log('Received filters from modal: ', filters);
      this.activeFilters = filters;
      let newRowData = this.rowData.filter(item => item !== undefined);

      Object.entries(filters).forEach(([filterName, stringsToMatch]) => {
        console.log(`Searching for ${filterName} within ${stringsToMatch}`);
        // Filter the data based on the current key and values
        newRowData = newRowData.filter(row => {
          if (!row) return false;
          if (stringsToMatch.includes(row[filterName])) {
            return true;
          }
          return false;
        });
      });
      this.filteredRowData = newRowData;
    }
  }

  private fileNameFromFileId(cid?: string) {
    return cid?.includes('#') ? cid.split('#')[1] : (cid ?? '');
  }

  fileNameFromUri(uri?: string): string {
    if (!uri) return '';
    const clean = uri.split('?')[0].split('#')[0];
    try {
      return decodeURIComponent(clean.split('/').pop() || '');
    } catch {
      return clean.split('/').pop() || '';
    }
  }

  private placeholdersKeyFor = (cid: string) => `uploadPlaceholders::${cid}`;

  // localStorage holds array of file_ids: ["file#name.pdf", ...]
  private loadPlaceholderIds(cidBare: string): string[] {
    try {
      return JSON.parse(localStorage.getItem(this.placeholdersKeyFor(cidBare)) || '[]');
    } catch {
      return [];
    }
  }
  private savePlaceholderIds(cidBare: string, ids: string[]) {
    localStorage.setItem(this.placeholdersKeyFor(cidBare), JSON.stringify(ids));
  }

  // keep pinnedTopRows exactly in step with storage
  pinnedTopRows: { file_id: string; title: string }[] = [];
  private readonly UPLOAD_PREFIX = 'Upload in Progress - ';

  private toPinnedRows(ids: string[]) {
    return ids.map(id => ({ file_id: id, title: `${this.UPLOAD_PREFIX}${id.split('#')[1]}` }));
  }

  private syncPinnedToStorage(cidBare: string) {
    this.pinnedTopRows = this.toPinnedRows(this.loadPlaceholderIds(cidBare));
  }

  private removePlaceholderId(cidBare: string, fileId: string) {
    const keep = this.loadPlaceholderIds(cidBare).filter(id => id.toLowerCase() !== fileId.toLowerCase());
    this.savePlaceholderIds(cidBare, keep);
    this.pinnedTopRows = this.toPinnedRows(keep);
  }

  private readonly LOCKED = new Set(['started', 'image_extraction', 'text_extraction', 'failed']);

  isRowLocked = (d?: RowData) => this.LOCKED.has((d?.processing_stage || '').toLowerCase());
}
